import gql from 'graphql-tag'

export const MUTATION_UPSERT_CAMPAIGN_VARIABLES_CONFIGURATION = gql`
  mutation UpsertCampaignVariablesConfiguration($data: CampaignVariablesConfigurationInput!) {
    upsertCampaignVariablesConfiguration(data: $data) {
      status
      message
      _id
    }
  }
`

const BEST_OFFER_MESSAGE_FRAGMENT = gql`
  fragment bestOfferMessage on CampaignVariablesConfigurationTextMetadataByOffer {
    repeatable_text
    validity_text
  }
`

const OFFER_TYPES_WITH_BEST_OFFER_FRAGMENT = gql`
  fragment offerTypesWithBestOffer on CampaignVariablesConfigurationTextByOffer {
    offer_sales {
      ...bestOfferMessage
    }
    combo_offer_sales {
      ...bestOfferMessage
    }
    takepay_offer_sales {
      ...bestOfferMessage
    }
    points_offer_sales {
      ...bestOfferMessage
    }
    cashback_percentage_offer_sales {
      ...bestOfferMessage
    }
    cashback_absolute_offer_sales {
      ...bestOfferMessage
    }
    percentage_offer_sales {
      ...bestOfferMessage
    }
  }
`

export const QUERY_GET_CAMPAIGN_VARIABLES_CONFIGURATION = gql`
  query CampaignVariablesConfiguration($variableType: CampaignVariablesConfigurationType!, $id: ID) {
    campaignVariablesConfiguration(variable_type: $variableType, _id: $id) {
      _id
      default
      variable_name
      channels {
        email {
          text {
            ...offerTypesWithBestOffer
          }
        }
        push {
          inner_message_title {
            ...offerTypesWithBestOffer
          }
          inner_message_text {
            ...offerTypesWithBestOffer
          }
          title {
            ...offerTypesWithBestOffer
          }
          text {
            ...offerTypesWithBestOffer
          }
        }
        whatsapp {
          text {
            ...offerTypesWithBestOffer
          }
        }
        sms {
          text {
            ...offerTypesWithBestOffer
          }
        }
      }
    }
  }

  ${OFFER_TYPES_WITH_BEST_OFFER_FRAGMENT}
  ${BEST_OFFER_MESSAGE_FRAGMENT}
`

export const QUERY_GET_DAYS_ON_OFFERS = gql`
  query GetDaysOnOffers($input: DaysOnOffersInput) {
    daysOnOffers(input: $input) {
      days {
        active
        day
      }
      max_days_interval_no_offer
    }
  }
`

export const QUERY_GET_PRODUCTS_AND_GROUPS = gql`
  query Products($search: String, $ids: [String], $skus: [String], $active: Boolean) {
    products(search: $search, ids: $ids, skus: $skus, active: $active) {
      products {
        _id
        sku
        ean
        eans
        name
        image
        measure_name
      }
    }
    productsGroups(search: $search) {
      productsGroups {
        _id
        name
        rules_exhibition
        image_url
        products {
          _id
          sku
          ean
          eans
          name
          image
          measure_name
        }
      }
    }
  }
`

export const QUERY_GET_CAMPAIGN_BY_ID = gql`
  query Campaign($id: ID!) {
    campaign(_id: $id) {
      _id
      title
      status
      description
      ... on WhatsappChannelCampaign {
        senders {
          name
          number
        }
        targeting_mode
      }
      initial_date
      final_date
      segments {
        _id
        name
        clients_qty
        updated_at
        clients_refreshed_at
      }
      products {
        sku
        name
      }
      message {
        send_date
        send_time
        subject
        ... on EmailChannelCampaignMessage {
          processed_html
          email_configuration_type
          simple_email_configuration {
            text
            subject
            header_image
            image
            click_to_call_button_text
            click_to_call_button_link
            footer
          }
          html_email_configuration {
            has_uploaded_file
            filename
            url
            uploaded_at
          }
          builder_email_configuration
        }
      }
    }
  }
`

export const QUERY_GET_BALANCE_BY_CHANNEL = gql`
  query BalanceByChannel {
    dispatchesBalanceByChannel {
      sms
      email
    }
  }
`

export const QUERY_NUMBER_OF_CONTACTS = gql`
  query GetNumberOfContacts($active: Boolean, $senders: [String]) {
    getNumberOfContacts(active: $active, senders: $senders) {
      total
      loyalty
      non_loyalty
    }
  }
`

export const QUERY_UPSERT_DRAFT_OMNI_CAMPAIGN = gql`
  mutation UpsertDraftOmniCampaign($data: OmniCampaignDraftInput!) {
    upsertDraftOmniCampaign(data: $data) {
      _id
      message
    }
  }
`

export const QUERY_UPSERT_OMNI_CAMPAIGN = gql`
  mutation UpsertOmniCampaign($data: OmniCampaignInput!) {
    upsertOmniCampaign(data: $data) {
      _id
      message
    }
  }
`

export const QUERY_GET_OMNI_CAMPAIGN = gql`
  query OmniCampaign($id: ID!) {
    omniCampaign(_id: $id) {
      title
      _id
      description
      initial_date
      start_time
      final_date
      end_time
      status
      units {
        _id
        id_erp
        name
        dataType
      }
      scheduled
      segments {
        _id
        name
        description
        fixed
        campaign_qty
        clients_qty
        clients_qty_at_campaign
        clients_refreshed_at
        created_at
        updated_at
      }
      products_count
      has_inactive_products
      offer_sales {
        ... on CampaignOfferSale {
          name
          sku
          finalized
          active
          ean
          eans
          product {
            _id
            sku
            category_id
            created_at
            eans
            ean
            erp_category_id
            erp_name
            image
            manually_edited
            name
            updated_at
            type
            measure_unit
            erp_categories_arr
          }
          price_1
          price_2
          measure_name
          maximum_per_client
          minimum_per_client
          group_name
          group_id
          group_rules_exhibition
        }
        ... on CampaignOfferSalesGroup {
          _id
          name
          group_rules_exhibition
          price_1
          price_2
          maximum_per_client
          minimum_per_client
          group_image_url
          offer_sales {
            name
            sku
            finalized
            active
            ean
            eans
            measure_name
            product {
              _id
              sku
              category_id
              created_at
              eans
              ean
              erp_category_id
              erp_name
              image
              manually_edited
              name
              updated_at
              type
              measure_unit
              erp_categories_arr
            }
          }
        }
      }
      percentage_offer_sales {
        ... on CampaignOfferSalePercentage {
          name
          sku
          finalized
          active
          ean
          eans
          measure_name
          maximum_per_client
          minimum_per_client
          percentage_discount
          group_name
          group_id
          group_rules_exhibition
          product {
            image
          }
        }
        ... on CampaignOfferSalesPercentageGroup {
          _id
          name
          group_rules_exhibition
          maximum_per_client
          minimum_per_client
          percentage_discount
          group_image_url
          percentage_offer_sales {
            name
            sku
            finalized
            active
            ean
            eans
            measure_name
            product {
              image
            }
          }
        }
      }
      takepay_offer_sales {
        ... on CampaignOfferSaleTakeAndPay {
          name
          sku
          finalized
          active
          ean
          eans
          maximum_per_client
          take
          pay
          measure_name
          group_name
          group_id
          group_rules_exhibition
          product {
            image
          }
        }
        ... on CampaignOfferSalesTakeAndPayGroup {
          _id
          name
          group_rules_exhibition
          maximum_per_client
          take
          pay
          group_image_url
          takepay_offer_sales {
            name
            sku
            finalized
            active
            ean
            eans
            measure_name
            product {
              image
            }
          }
        }
      }
      combo_offer_sales {
        name
        combo_id
        image
        active
        maximum_per_client
        trigger_products {
          ... on CampaignTriggerProduct {
            name
            sku
            ean
            eans
            quantity
            group_name
            group_id
          }
          ... on CampaignTriggerProductGroup {
            _id
            name
            quantity
            trigger_products {
              name
              sku
              ean
              eans
            }
          }
        }
        target_products {
          ... on CampaignTargetProduct {
            name
            sku
            ean
            eans
            percentage_discount
            group_name
            group_id
          }
          ... on CampaignTargetProductGroup {
            _id
            name
            percentage_discount
            target_products {
              name
              sku
              ean
              eans
            }
          }
        }
        rules_exhibition
      }
      cashback_absolute_offer_sales {
        ... on CampaignOfferSaleCashbackAbsolute {
          name
          sku
          finalized
          active
          ean
          eans
          cashback_absolute
          expiration_cashback {
            type
            expiration_in_days
            expires_at
          }
          maximum_per_client
          minimum_per_client
          price
          group_name
          group_id
          group_rules_exhibition
          image_url
          product {
            image
          }
        }
        ... on CampaignOfferSalesCashbackAbsoluteGroup {
          _id
          name
          group_rules_exhibition
          cashback_absolute
          expiration_cashback {
            type
            expiration_in_days
            expires_at
          }
          maximum_per_client
          minimum_per_client
          price
          group_image_url
          cashback_absolute_offer_sales {
            name
            sku
            finalized
            active
            ean
            eans
            product {
              image
            }
          }
        }
      }
      cashback_percentage_offer_sales {
        ... on CampaignOfferSaleCashbackPercentage {
          name
          sku
          finalized
          active
          ean
          eans
          cashback_percentage
          expiration_cashback {
            type
            expiration_in_days
            expires_at
          }
          maximum_per_client
          minimum_per_client
          price_1
          group_name
          group_id
          group_rules_exhibition
          product {
            image
          }
        }
        ... on CampaignOfferSalesCashbackPercentageGroup {
          _id
          name
          group_rules_exhibition
          cashback_percentage
          expiration_cashback {
            type
            expiration_in_days
            expires_at
          }
          maximum_per_client
          minimum_per_client
          price_1
          group_image_url
          cashback_percentage_offer_sales {
            name
            sku
            finalized
            active
            ean
            eans
            product {
              image
            }
          }
        }
      }
      points_offer_sales {
        ... on CampaignOfferSalePoints {
          name
          sku
          finalized
          active
          ean
          eans
          additional_points_rate
          maximum_per_client
          minimum_per_client
          price
          group_name
          group_id
          group_rules_exhibition
          product {
            image
          }
        }
        ... on CampaignOfferSalesPointsGroup {
          _id
          name
          group_rules_exhibition
          additional_points_rate
          maximum_per_client
          minimum_per_client
          price
          group_image_url
          points_offer_sales {
            name
            sku
            finalized
            active
            ean
            eans
            product {
              image
            }
          }
        }
      }
      offer_schedules {
        name
        offer_schedule {
          _id
          auto_id
          has_results
          finalized
          description
          products_count
          active
          draft
          end_date
          created_at
          updated_at
          departments {
            _id
            name
          }
          segments {
            _id
            name
            description
            fixed
            campaign_qty
            clients_qty
            clients_qty_at_campaign
            clients_refreshed_at
            created_at
            updated_at
          }
          start_date
          status
        }
        from_franchise
        start_date
        end_date
        units
        offer_sales {
          ... on CampaignOfferSale {
            name
            sku
            finalized
            active
            ean
            eans
            measure_name
            product {
              _id
              sku
              category_id
              created_at
              eans
              ean
              erp_category_id
              erp_name
              image
              manually_edited
              name
              updated_at
              type
              measure_unit
              erp_categories_arr
            }
            price_1
            price_2
            maximum_per_client
            minimum_per_client
            group_name
            group_id
            group_rules_exhibition
          }
          ... on CampaignOfferSalesGroup {
            _id
            name
            group_rules_exhibition
            price_1
            price_2
            maximum_per_client
            minimum_per_client
            group_image_url
            offer_sales {
              name
              sku
              finalized
              active
              ean
              eans
              measure_name
              product {
                image
              }
            }
          }
        }
        percentage_offer_sales {
          ... on CampaignOfferSalePercentage {
            name
            sku
            finalized
            active
            ean
            eans
            maximum_per_client
            minimum_per_client
            percentage_discount
            group_name
            group_id
            group_rules_exhibition
            measure_name
            product {
              image
            }
          }
          ... on CampaignOfferSalesPercentageGroup {
            _id
            name
            group_rules_exhibition
            maximum_per_client
            minimum_per_client
            percentage_discount
            group_image_url
            percentage_offer_sales {
              name
              sku
              finalized
              active
              ean
              eans
              measure_name
              product {
                image
              }
            }
          }
        }
        takepay_offer_sales {
          ... on CampaignOfferSaleTakeAndPay {
            name
            sku
            finalized
            active
            ean
            eans
            maximum_per_client
            take
            pay
            group_name
            group_id
            group_rules_exhibition
            measure_name
            product {
              image
            }
          }
          ... on CampaignOfferSalesTakeAndPayGroup {
            _id
            name
            group_rules_exhibition
            maximum_per_client
            take
            pay
            group_image_url
            takepay_offer_sales {
              name
              sku
              finalized
              active
              ean
              eans
              measure_name
              product {
                image
              }
            }
          }
        }
        combo_offer_sales {
          name
          combo_id
          image
          active
          maximum_per_client
          trigger_products {
            ... on CampaignTriggerProduct {
              name
              sku
              ean
              eans
              quantity
              group_name
              group_id
            }
            ... on CampaignTriggerProductGroup {
              _id
              name
              quantity
              trigger_products {
                name
                sku
                ean
                eans
              }
            }
          }
          target_products {
            ... on CampaignTargetProduct {
              name
              sku
              ean
              eans
              percentage_discount
              group_name
              group_id
            }
            ... on CampaignTargetProductGroup {
              _id
              name
              percentage_discount
              target_products {
                name
                sku
                ean
                eans
              }
            }
          }
          rules_exhibition
        }
        cashback_absolute_offer_sales {
          ... on CampaignOfferSaleCashbackAbsolute {
            name
            sku
            finalized
            active
            ean
            eans
            cashback_absolute
            expiration_cashback {
              type
              expiration_in_days
              expires_at
            }
            maximum_per_client
            minimum_per_client
            price
            group_name
            group_id
            group_rules_exhibition
            measure_name
            product {
              image
            }
          }
          ... on CampaignOfferSalesCashbackAbsoluteGroup {
            _id
            name
            group_rules_exhibition
            cashback_absolute
            expiration_cashback {
              type
              expiration_in_days
              expires_at
            }
            maximum_per_client
            minimum_per_client
            price
            group_image_url
            cashback_absolute_offer_sales {
              name
              sku
              finalized
              active
              ean
              eans
              measure_name
              product {
                image
              }
            }
          }
        }
        cashback_percentage_offer_sales {
          ... on CampaignOfferSaleCashbackPercentage {
            name
            sku
            finalized
            active
            ean
            eans
            cashback_percentage
            expiration_cashback {
              type
              expiration_in_days
              expires_at
            }
            maximum_per_client
            minimum_per_client
            price_1
            group_name
            group_id
            group_rules_exhibition
            measure_name
            product {
              image
            }
          }
          ... on CampaignOfferSalesCashbackPercentageGroup {
            _id
            name
            group_rules_exhibition
            cashback_percentage
            expiration_cashback {
              type
              expiration_in_days
              expires_at
            }
            maximum_per_client
            minimum_per_client
            price_1
            group_image_url
            cashback_percentage_offer_sales {
              name
              sku
              finalized
              active
              ean
              eans
              measure_name
              product {
                image
              }
            }
          }
        }
        points_offer_sales {
          ... on CampaignOfferSalePoints {
            name
            sku
            finalized
            active
            ean
            eans
            additional_points_rate
            maximum_per_client
            minimum_per_client
            price
            group_name
            group_id
            group_rules_exhibition
            measure_name
            product {
              image
            }
          }
          ... on CampaignOfferSalesPointsGroup {
            _id
            name
            group_rules_exhibition
            additional_points_rate
            maximum_per_client
            minimum_per_client
            price
            group_image_url
            points_offer_sales {
              name
              sku
              finalized
              active
              ean
              eans
              measure_name
              product {
                image
              }
            }
          }
        }
      }
      client_targets_distribution {
        actives
        recent_inactives
        losses
        at_risk
        never_bought
      }
      product_targets {
        repurchase
        upsell
      }
      initial_date
      final_date
      unique_products {
        sku
        name
        image
      }
      offer_sales {
        ... on CampaignOfferSale {
          name
          sku
          ean
          eans
          price_1
          price_2
          active
          product {
            sku
            ean
            eans
            name
          }
        }
        ... on CampaignOfferSalesGroup {
          _id
          name
          price_1
          price_2
          offer_sales {
            name
            sku
            ean
            eans
            active
            product {
              sku
              ean
              eans
              name
            }
          }
        }
      }
      percentage_offer_sales {
        ... on CampaignOfferSalePercentage {
          name
          sku
          active
          ean
          eans
          percentage_discount
          maximum_per_client
          minimum_per_client
          measure_name
          product {
            sku
            ean
            eans
            name
          }
        }
        ... on CampaignOfferSalesPercentageGroup {
          _id
          name
          percentage_discount
          maximum_per_client
          minimum_per_client
          group_image_url
          percentage_offer_sales {
            name
            sku
            active
            ean
            eans
            finalized
            measure_name
            product {
              eans
              ean
            }
          }
        }
      }
      takepay_offer_sales {
        ... on CampaignOfferSaleTakeAndPay {
          name
          sku
          active
          ean
          eans
          take
          pay
          maximum_per_client
          measure_name
          product {
            sku
            ean
            eans
            name
          }
        }
        ... on CampaignOfferSalesTakeAndPayGroup {
          _id
          name
          take
          pay
          maximum_per_client
          group_image_url
          takepay_offer_sales {
            name
            sku
            active
            ean
            eans
            finalized
            measure_name
            product {
              eans
              ean
            }
          }
        }
      }
      combo_offer_sales {
        name
        maximum_per_client
        image
        active
        trigger_products {
          ... on CampaignTriggerProduct {
            quantity
            name
            sku
            ean
            eans
            product {
              sku
              ean
              eans
              name
            }
          }
          ... on CampaignTriggerProductGroup {
            _id
            name
            quantity
            trigger_products {
              name
              sku
              ean
              eans
              product {
                _id
                sku
                ean
                eans
                name
              }
            }
          }
        }
        target_products {
          ... on CampaignTargetProduct {
            percentage_discount
            name
            sku
            ean
            eans
            product {
              sku
              ean
              eans
              name
            }
          }
          ... on CampaignTargetProductGroup {
            _id
            name
            percentage_discount
            target_products {
              name
              sku
              ean
              eans
            }
          }
        }
      }
      created_at
      updated_at
      messages_dispatcher {
        settings {
          model
          limit_per_consumer {
            quantity
            limiter
          }
          product_variable_type
          schedule_settings {
            id
            active
            initial_hour
            final_hour
            selector_channels_type
            week_day
            send_date
            channels {
              sms {
                active
                priority
                message {
                  text
                  amount_best_offers
                }
              }
              whatsapp {
                active
                priority
                message {
                  text
                  amount_best_offers
                  attachment {
                    filename
                    mimetype
                    thumbnail_url
                    data_url
                  }
                  type
                }
                senders {
                  name
                  number
                }
                targeting_mode
              }
              push {
                active
                priority
                message {
                  action {
                    link
                    reference_id
                    type
                  }
                  text
                  amount_best_offers
                  title
                  amount_best_offers_title
                  inner_message_title
                  inner_message_text
                  inner_amount_best_offers
                  inner_amount_best_offers_title
                  image_url
                }
              }
              email {
                active
                priority
                message {
                  text
                  amount_best_offers
                  subject
                  amount_best_offers_subject
                  email_configuration_type
                  processed_html
                  builder_email_configuration
                  html_email_configuration {
                    text
                    amount_best_offers
                    has_uploaded_file
                    filename
                    url
                    amount_best_offers_subject
                    uploaded_at
                  }
                  simple_email_configuration {
                    text
                    amount_best_offers
                    subject
                    amount_best_offers_subject
                    header_image
                    image
                    click_to_call_button_text
                    click_to_call_button_link
                    footer
                  }
                }
              }
            }
          }

          campaign_variable_configurations {
            _id
            default
            channels {
              email {
                text {
                  ...offerTypesWithBestOffer
                }
              }
              push {
                inner_message_title {
                  ...offerTypesWithBestOffer
                }
                inner_message_text {
                  ...offerTypesWithBestOffer
                }
                title {
                  ...offerTypesWithBestOffer
                }
                text {
                  ...offerTypesWithBestOffer
                }
              }
              whatsapp {
                text {
                  ...offerTypesWithBestOffer
                }
              }
              sms {
                text {
                  ...offerTypesWithBestOffer
                }
              }
            }
          }
        }
        budget {
          limiter
          channels {
            sms
            whatsapp
            push
            email
          }
        }
      }
      messages_dispatches_quantity
      messages_dispatches_channels
    }
  }

  ${OFFER_TYPES_WITH_BEST_OFFER_FRAGMENT}
  ${BEST_OFFER_MESSAGE_FRAGMENT}
`

// TODO: Colocar a query completa, e ver com o pessoal de back a migração das campanhas antigas pro modelo mais novo
export const QUERY_LIST_OMNI_CAMPAIGNS = gql`
  query omniCampaigns(
    $page: Int
    $sortDirection: [SortDirection]
    $search: String
    $status: [OmniCampaignStatus]
    $units: [String]
    $channels: [ChannelEnum]
    $products: [String]
    $sort: [String]
    $pageSize: Int
  ) {
    omniCampaigns(
      page: $page
      sortDirection: $sortDirection
      search: $search
      status: $status
      units: $units
      channels: $channels
      products: $products
      sort: $sort
      pageSize: $pageSize
    ) {
      omni_campaigns {
        title
        _id
        description
        initial_date
        start_time
        final_date
        end_time
        status
        units {
          _id
          id_erp
          name
          dataType
        }
        created_at
        updated_at
        messages_dispatches_quantity
        messages_dispatches_channels
        products_count
      }
      records
      page
    }
  }
`

export const QUERY_DELETE_OMNI_CAMPAIGN = gql`
  mutation DeleteOmniCampaign($id: ID!) {
    deleteOmniCampaign(_id: $id) {
      _id
      message
    }
  }
`

export const QUERY_UNSCHEDULE_OMNI_CAMPAIGN = gql`
  mutation UnscheduleOmniCampaign($id: ID!) {
    unscheduleOmniCampaign(_id: $id) {
      _id
      message
    }
  }
`

const METRICS_VALUES_FRAGMENT = gql`
  fragment metricsValues on MetricValues {
    count
    revenue
    tickets
    average_ticket
    quantity
    clients
  }
`

export const QUERY_METRICS_OMNI_CAMPAIGN_BY_UNITS = gql`
  query OmniCampaignMetricsByUnits($id: ID!, $units: [String], $includeEcommerce: Boolean, $onlyEcommerce: Boolean) {
    omniCampaign(_id: $id) {
      metrics(units: $units, includeEcommerce: $includeEcommerce, onlyEcommerce: $onlyEcommerce) {
        clients_metrics {
          before_bought {
            received {
              actives {
                clients
                revenue
              }
              at_risk {
                clients
                revenue
              }
              losses {
                clients
                revenue
              }
              never_bought {
                clients
                revenue
              }
              recent_inactives {
                clients
                revenue
              }
            }
            received_and_bought {
              actives {
                clients
                revenue
              }
              at_risk {
                clients
                revenue
              }
              losses {
                clients
                revenue
              }
              never_bought {
                clients
                revenue
              }
              recent_inactives {
                clients
                revenue
              }
            }
            received_and_bought_with_offer_products {
              recent_inactives {
                clients
                revenue
              }
              actives {
                clients
                revenue
              }
              losses {
                clients
                revenue
              }
              at_risk {
                clients
                revenue
              }
              never_bought {
                clients
                revenue
              }
            }
            received_and_bought_with_received_product {
              recent_inactives {
                clients
                revenue
              }
              actives {
                clients
                revenue
              }
              losses {
                clients
                revenue
              }
              at_risk {
                clients
                revenue
              }
              never_bought {
                clients
                revenue
              }
            }
          }
          general {
            identified {
              count
              revenue
              tickets
              average_ticket
            }
            received {
              count
              revenue
              tickets
              average_ticket
            }
            received_and_bought {
              count
              revenue
              tickets
              average_ticket
            }
            received_and_bought_with_offer_products {
              ...metricsValues
            }
            received_and_bought_with_received_product {
              ...metricsValues
            }
            total {
              revenue
            }
          }
          channels {
            email {
              sent {
                count
              }
              received {
                count
                revenue
                tickets
                average_ticket
              }
              received_and_bought {
                count
                revenue
                tickets
                average_ticket
              }
              received_and_bought_with_offer_products {
                ...metricsValues
              }
              received_and_bought_with_received_product {
                ...metricsValues
              }
              total {
                revenue
              }
            }
            push {
              sent {
                count
              }
              received {
                count
                revenue
                tickets
                average_ticket
              }
              received_and_bought {
                count
                revenue
                tickets
                average_ticket
              }
              received_and_bought_with_offer_products {
                ...metricsValues
              }
              received_and_bought_with_received_product {
                ...metricsValues
              }
              total {
                revenue
              }
            }
            whatsapp {
              sent {
                count
              }
              received {
                count
                revenue
                tickets
                average_ticket
              }
              received_and_bought {
                count
                revenue
                tickets
                average_ticket
              }
              received_and_bought_with_offer_products {
                ...metricsValues
              }
              received_and_bought_with_received_product {
                ...metricsValues
              }
              total {
                revenue
              }
            }
            sms {
              sent {
                count
              }
              identified {
                count
                revenue
                tickets
                average_ticket
              }
              received {
                count
                revenue
                tickets
                average_ticket
              }
              received_and_bought {
                count
                revenue
                tickets
                average_ticket
              }
              received_and_bought_with_offer_products {
                ...metricsValues
              }
              received_and_bought_with_received_product {
                ...metricsValues
              }
              total {
                revenue
              }
            }
          }
        }
        general_metrics {
          channels {
            email {
              received {
                messages
              }
              received_and_bought {
                messages
              }
              received_and_bought_with_offer_products {
                messages
              }
              received_and_bought_with_received_product {
                messages
              }
              sent {
                messages
              }
            }
            push {
              received {
                messages
              }
              received_and_bought {
                messages
              }
              received_and_bought_with_offer_products {
                messages
              }
              received_and_bought_with_received_product {
                messages
              }
              sent {
                messages
              }
            }
            sms {
              received {
                messages
              }
              received_and_bought {
                messages
              }
              received_and_bought_with_offer_products {
                messages
              }
              received_and_bought_with_received_product {
                messages
              }
              sent {
                messages
              }
            }
            whatsapp {
              received {
                messages
              }
              received_and_bought {
                messages
              }
              received_and_bought_with_offer_products {
                messages
              }
              received_and_bought_with_received_product {
                messages
              }
              sent {
                messages
              }
            }
          }
        }
        analytic_metrics {
          channels {
            whatsapp {
              credits
              sending_credits
              validation_credits
              price
              sending_price
              validation_price
              received_roi
              opened_roi
              average_ticket_received_variation
            }
            sms {
              credits
              sending_credits
              validation_credits
              price
              sending_price
              validation_price
              received_roi
              opened_roi
              average_ticket_received_variation
            }
            push {
              credits
              sending_credits
              validation_credits
              price
              sending_price
              validation_price
              received_roi
              opened_roi
              average_ticket_received_variation
            }
            email {
              credits
              sending_credits
              validation_credits
              price
              sending_price
              validation_price
              received_roi
              opened_roi
              average_ticket_received_variation
            }
          }
          general {
            credits
            sending_credits
            validation_credits
            price
            sending_price
            validation_price
            received_roi
            opened_roi
            average_ticket_received_variation
          }
        }
        products_metrics {
          related_table {
            product {
              sku
              name
            }
            tickets
            total_tickets
          }
          table {
            opened {
              clients
              quantity
              revenue
              tickets
            }
            product {
              sku
              name
              _id
            }
            received {
              clients
              quantity
              revenue
              tickets
            }
            total {
              clients
              quantity
              revenue
              tickets
            }
            message_received {
              clients
            }
            received_upsell {
              clients
              quantity
              revenue
              tickets
            }
            opened_upsell {
              clients
              quantity
              revenue
              tickets
            }
            total_upsell {
              clients
              quantity
              revenue
              tickets
            }
          }
          totals {
            opened {
              clients
              quantity
              revenue
              tickets
            }
            received {
              clients
              quantity
              revenue
              tickets
            }
            total {
              clients
              quantity
              revenue
              tickets
            }
            message_received {
              clients
            }
            received_upsell {
              clients
              quantity
              revenue
              tickets
            }
            opened_upsell {
              clients
              quantity
              revenue
              tickets
            }
            total_upsell {
              clients
              quantity
              revenue
              tickets
            }
          }
        }
      }
    }
  }

  ${METRICS_VALUES_FRAGMENT}
`

export const QUERY_CHANNEL_SHOOTING_HISTORY = gql`
  query MessagesHistory($id: ID!, $page: Int, $pageSize: Int, $channels: [ChannelEnum], $sort: [String], $sortDirection: [SortDirection]) {
    messagesHistory(_id: $id, page: $page, pageSize: $pageSize, channels: $channels, sort: $sort, sortDirection: $sortDirection) {
      messages_history {
        client_id
        contact
        content
        date
        price
        status
      }
      records
      page
    }
  }
`

export const QUERY_FINISH_OMNI_CAMPAIGN = gql`
  mutation FinishOmniCampaign($id: ID!) {
    finishOmniCampaign(_id: $id) {
      _id
      message
    }
  }
`

export const QUERY_AGGREGATED_CLIENTS_STATUS_INFO = gql`
  query GetAggregatedClientStatusInfo($segments_ids: [ID!]!) {
    getAggregatedClientStatusInfo(segments_ids: $segments_ids) {
      all {
        ... on AggregatedClientStatusInfo {
          client_active_count
          client_at_risk_count
          client_losses_count
          client_never_bought_count
          client_recent_inactive_count
          total_count
        }
      }
    }
  }
`

export const QUERY_GET_MESSAGE_BY_TYPE = gql`
  query GetMessageByType {
    getMessageByType(type: OPTOUT_SUFFIX) {
      interpolated_text
      active
    }
  }
`

export const QUERY_GET_OMNI_CAMPAIGN_TICKETS_BY_HOURS = gql`
  query OmniCampaignTicketsByHour($id: ID!, $units: [String]) {
    omniCampaignTicketsByHour(_id: $id, units: $units) {
      metrics {
        hour
        received {
          tickets
        }
        received_and_bought {
          tickets
        }
        received_and_bought_with_offer_products {
          tickets
        }
        received_and_bought_with_received_product {
          tickets
        }
      }
    }
  }
`

export const QUERY_GET_OMNI_CAMPAIGN_TICKETS_BY_GENDER_AND_AGE = gql`
  query OmniCampaignAverageTicketByGenderAndAge($id: ID!, $units: [String]) {
    omniCampaignAverageTicketByGenderAndAge(_id: $id, units: $units) {
      metrics {
        age_range
        received {
          female {
            average_ticket
            clients
          }
          male {
            average_ticket
            clients
          }
          unknown_gender {
            average_ticket
            clients
          }
        }
        received_and_bought {
          female {
            average_ticket
            clients
          }
          male {
            average_ticket
            clients
          }
          unknown_gender {
            average_ticket
            clients
          }
        }
        received_and_bought_with_offer_products {
          male {
            clients
            average_ticket
          }
          female {
            clients
            average_ticket
          }
          unknown_gender {
            clients
            average_ticket
          }
        }
        received_and_bought_with_received_product {
          male {
            clients
            average_ticket
          }
          female {
            clients
            average_ticket
          }
          unknown_gender {
            clients
            average_ticket
          }
        }
      }
      totals {
        received {
          female {
            clients
          }
          male {
            clients
          }
          unknown_gender {
            clients
          }
        }
        received_and_bought {
          female {
            clients
          }
          male {
            clients
          }
          unknown_gender {
            clients
          }
        }
        received_and_bought_with_offer_products {
          male {
            clients
          }
          female {
            clients
          }
          unknown_gender {
            clients
          }
        }
        received_and_bought_with_received_product {
          male {
            clients
          }
          female {
            clients
          }
          unknown_gender {
            clients
          }
        }
      }
    }
  }
`

export const QUERY_GET_OMNI_CAMPAIGN_CHANNELS_ITEMS = gql`
  query OmniCampaignAverageItemsByClientMessageReceiving($id: ID!, $units: [String]) {
    omniCampaignAverageItemsByClientMessageReceiving(_id: $id, units: $units) {
      metrics {
        average_items_range
        received_and_bought_clients
        not_received_and_bought_clients
      }
    }
  }
`

const SENT_METRICS_FRAGMENT = gql`
  fragment sentMetrics on MessagesCampaignsMetricsMetricKpi {
    sent {
      value
      date
    }
  }
`

export const QUERY_GET_METRICS = gql`
  query MetricsKpi(
    $initialDate: String!
    $finalDate: String!
    $groupBy: MetricGroupBy
    $units: [ID]
    $campaignIds: [ID]
    $productIds: [String]
    $useMessagesCampaignsMetrics: Boolean
  ) {
    metricsKpi(
      initialDate: $initialDate
      finalDate: $finalDate
      units: $units
      campaignIds: $campaignIds
      productIds: $productIds
      groupBy: $groupBy
      useMessagesCampaignsMetrics: $useMessagesCampaignsMetrics
    ) {
      revenue {
        value
        date
      }
      total_coupons {
        value
        date
      }
      unique_customers {
        value
        date
      }
      messages_campaigns {
        general {
          ...sentMetrics
        }
        channels {
          whatsapp {
            ...sentMetrics
          }
          sms {
            ...sentMetrics
          }
          push {
            ...sentMetrics
          }
          email {
            ...sentMetrics
          }
        }
      }
    }
  }

  ${SENT_METRICS_FRAGMENT}
`

export const QUERY_CLONE_OMNI_CAMPAIGN = gql`
  mutation CloneOmniCampaign($id: ID!) {
    cloneOmniCampaign(_id: $id) {
      _id
      message
    }
  }
`

export const QUERY_GET_AGGREGATED_CLIENTS_STATUS_INFO = gql`
  query GetAggregatedClientStatusInfo($segments_ids: [ID!]!) {
    getAggregatedClientStatusInfo(segments_ids: $segments_ids) {
      email {
        ... on AggregatedClientStatusInfo {
          client_active_count
          client_at_risk_count
          client_losses_count
          client_never_bought_count
          client_recent_inactive_count
          total_count
        }
      }
      push {
        ... on AggregatedClientStatusInfo {
          client_active_count
          client_at_risk_count
          client_losses_count
          client_never_bought_count
          client_recent_inactive_count
          total_count
        }
      }
      sms {
        ... on AggregatedClientStatusInfo {
          client_active_count
          client_at_risk_count
          client_losses_count
          client_never_bought_count
          client_recent_inactive_count
          total_count
        }
      }
      whatsapp {
        ... on AggregatedClientStatusInfoWhatsapp {
          client_active_count
          client_at_risk_count
          client_losses_count
          client_never_bought_count
          client_recent_inactive_count
          client_non_loyalty_count
          total_count
        }
      }
    }
  }
`

export const QUERY_GET_DEFAULT_COLORS_HOTSITE = gql`
  query Account {
    account {
      channels {
        hotsite {
          urls
          configuration {
            assets {
              colors {
                finish {
                  main
                }
              }
            }
          }
        }
      }
    }
  }
`
